/** @prettier */

import {Grid, Container, Stack, Box, Card, CardContent} from '@mui/material';
import {
  useLoaderData,
  useParams,
  LoaderFunctionArgs,
  useNavigate,
} from 'react-router-dom';
import {utcToZonedTime} from 'date-fns-tz';
import {useTranslation} from 'react-i18next';

//MTH component import
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import CardContents from '../../component/CardContents';
//API import
import {
  getSettingSavingEnergy,
  getCompositionsSitesBySiteID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {numberWithPeriod} from '../../functions/convertString';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';
import {iconBlack} from '../../define';

type loaderReturn = {
  savingEnergy: apiIF.res.getSettingSavingEnergy;
  siteName: string;
  timezone: string;
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //省エネ設定を取得
    promises.push(getSettingSavingEnergy(params.siteID));
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //全てのユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const result = await Promise.all(promises);

    const savingEnergy = result[0] as apiIF.res.getSettingSavingEnergy;
    const siteInfo = result[1] as apiIF.res.getCompositionsSitesBySiteID;
    const unitExistence = result[2] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      siteInfo['site-authority'] !== 'admin'
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      savingEnergy: savingEnergy,
      siteName: siteInfo['site-name'],
      timezone: siteInfo.timezone,
    };
  } catch {
    return undefined;
  }
}

/**
 * 省エネ制御
 * @returns JSX.Element
 */
const SavingEnergyMain = () => {
  const loaderData = useLoaderData() as loaderReturn | undefined;
  const {siteID} = useParams();
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  /** サイト名称 */
  const siteName = loaderData && loaderData.siteName;
  /** 有効/無効に対応する表示 */
  const activeDisplay: {
    [key in apiIF.res.getSettingSavingEnergy['control']]: string;
  } = {
    active: trans('SavingEnergyMain.control.active'),
    inactive: trans('SavingEnergyMain.control.inactive'),
  };
  /** 省エネの有効/無効 */
  const controlStatus =
    loaderData === undefined
      ? activeDisplay.inactive
      : activeDisplay[loaderData.savingEnergy.control];
  /** 年間目標消費電力 */
  let targetYearPowerConsumption = '--';
  /** 当月の目標消費電力 */
  let targetMonthPowerConsumption = '0';
  if (loaderData) {
    //年間目標消費電力の取得
    targetYearPowerConsumption = numberWithPeriod(
      loaderData.savingEnergy['target-power-consumption']
    );

    /**
     * 月名（フルスペル）を取得する関数
     * @param date 日付
     * @returns 月名
     */
    const getLongMonthName = (date: Date) => {
      /** 月の名前 */
      const monthFullTable: (keyof apiIF.res.getSettingSavingEnergy['monthly-coefficient'])[] =
        [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december',
        ];
      return monthFullTable[date.getMonth()];
    };
    /** 当月の月名(フルスペル) */
    const nowMonth = getLongMonthName(
      utcToZonedTime(new Date(), loaderData.timezone)
    );
    /** 月毎の係数を積算した値 */
    const sumCoefficient = Object.values(
      loaderData.savingEnergy['monthly-coefficient']
    ).reduce((result, coefficient) => {
      result += Number(coefficient);
      return result;
    }, 0);
    //当月の目標消費電力量を計算
    targetMonthPowerConsumption = numberWithPeriod(
      String(
        Math.round(
          Number(loaderData.savingEnergy['target-power-consumption']) *
            (Number(loaderData.savingEnergy['monthly-coefficient'][nowMonth]) /
              sumCoefficient)
        )
      )
    );
  }

  /**
   * 歯車メニューリスト
   * @returns メニュー一覧
   */
  const gearMenuItem = (): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: 'mv-btnSavingEnergyMainEdit',
        label: trans('SavingEnergyMain.menuItem.edit'),
        onClick: () => {
          navigate(
            `/pages/my-site/site-detail/${siteID}/saving-energy-setting`
          );
        },
        icon: {
          className: 'savingEnergyMain__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(
            `SavingEnergyMain.image.alternativeText.${iconBlack.EDIT.alt}`
          ),
        },
      },
    ];
  };

  return (
    <Container fixed className="savingEnergy">
      <Stack spacing={3}>
        {/** タイトルと説明 */}
        <Box>
          <h1>{trans('SavingEnergyMain.title')}</h1>
          <h2>{trans('SavingEnergyMain.explanation')}</h2>
          <h3>*&nbsp;{trans('SavingEnergyMain.caution')}</h3>
        </Box>
        <Box className="savingEnergy__overviewContainer">
          <Stack>
            {/** サイト名 */}
            <Box className="savingEnergy__overviewContainer__siteName">
              {siteName}
            </Box>
            {/** 概要 */}
            <Card className="savingEnergy__overviewContainer__overview">
              <CardContent>
                <Stack spacing={4}>
                  {/** タイトルと編集ボタン */}
                  <Box className="savingEnergy__overviewContainer__overview__title">
                    <Grid container>
                      <Grid item xs>
                        {trans('SavingEnergyMain.displayTitle')}
                      </Grid>
                      <Grid item xs="auto">
                        <GearMenu
                          id={`mv-btnSavingEnergyMain`}
                          menuItem={gearMenuItem()}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  {/** 設定値 */}
                  <Box className="savingEnergy__overviewContainer__overview__settingValue">
                    <Stack spacing={2}>
                      {/** 制御状態 */}
                      <Box>
                        <CardContents
                          valueDescription={trans(
                            'SavingEnergyMain.controlStatus'
                          )}
                          value={controlStatus}
                          class="savingEnergy__overviewContainer__overview__settingValue__item"
                        />
                      </Box>
                      {/** 年間目標電力量 */}
                      <Box>
                        <CardContents
                          valueDescription={trans(
                            'SavingEnergyMain.targetPowerConsumption'
                          )}
                          value={`${
                            targetYearPowerConsumption === ''
                              ? '---'
                              : targetYearPowerConsumption
                          }kWh`}
                          class="savingEnergy__overviewContainer__overview__settingValue__item"
                        />
                      </Box>
                      {/** 当月目標電力量 */}
                      <Box>
                        <CardContents
                          valueDescription={trans(
                            'SavingEnergyMain.targetThisMonth'
                          )}
                          value={`${targetMonthPowerConsumption}kWh`}
                          class="savingEnergy__overviewContainer__overview__settingValue__item"
                        />
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
};
export default SavingEnergyMain;
